/**
 * Monaco editor bootstrap.
 *
 * Workers are bundled LOCALLY via Vite `?worker` imports (no CDN). We wire
 * `self.MonacoEnvironment.getWorker` so every editor language uses one of the
 * bundled worker chunks, then configure the TypeScript language service to
 * accept plain JS and to load the sandbox declarations (`SANDBOX_DTS`).
 */
import * as monaco from 'monaco-editor';
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';
import { SANDBOX_DTS } from './sandboxTypes';

// `MonacoEnvironment` is not declared on `Window` in every version; assign it
// on `self` (globaThis in the main thread) with the monaco-exported type.
(self as unknown as { MonacoEnvironment: monaco.Environment }).MonacoEnvironment = {
  getWorker(_workerId: string, label: string) {
    switch (label) {
      case 'json':
        return new jsonWorker();
      case 'css':
      case 'scss':
      case 'less':
        return new cssWorker();
      case 'html':
      case 'handlebars':
      case 'razor':
        return new htmlWorker();
      case 'typescript':
      case 'javascript':
        return new tsWorker();
      default:
        return new editorWorker();
    }
  },
};

// Allow TypeScript to type-check JS files (the webhook code is `javascript`,
// but the TS language worker provides IntelliSense for it).
monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
  target: monaco.languages.typescript.ScriptTarget.ES2020,
  allowNonTsExtensions: true,
  allowJs: true,
  checkJs: false,
  noEmit: true,
  lib: ['es2020', 'dom'],
});

// Expose the sandbox globals for autocompletion inside the editor.
monaco.languages.typescript.javascriptDefaults.addExtraLib(SANDBOX_DTS, 'ts:sandbox.d.ts');

/**
 * Apply the current theme to every Monaco instance.
 */
export function applyTheme(isDark: boolean): void {
  monaco.editor.setTheme(isDark ? 'vs-dark' : 'vs');
}

/**
 * Derive the active Monaco theme name from the HTML `dark` class.
 */
export function getMonacoTheme(): 'vs-dark' | 'vs' {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
    ? 'vs-dark'
    : 'vs';
}

export { monaco };